import { computed, ref } from "vue";
import { Service } from "@/constants/cloud-apps.ts";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { storeToRefs } from "pinia";
import { isCloudAppConnected } from "@/_helpers/cloud-security.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { SecurityAwarenessAction } from "@/constants/security-awareness.ts";
import { ModalWidth } from "@/constants/modals.ts";
import AllowSecurityAwarenessModal from "@/components/modals/AllowSecurityAwarenessModal.vue";
import { i18n } from "@/plugins/i18n.ts";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export function useSecurityAwarenessEmptyState() {
  const cloudAppsStore = useCloudAppsStore();
  const securityAwarenessStore = useSecurityAwarenessModule();
  const dialogsStore = useDialogsStore();
  const { metadata } = storeToRefs(securityAwarenessStore);

  const showEmptyStateSkeletonLoader = ref(false);

  // is Office OR Google connected?
  const isCloudAppsConnected = computed(() => {
    return isGoogleConnected.value || isOfficeConnected.value;
  });

  const isGoogleConnected = computed(() => {
    return isCloudAppConnected(Service.G_SUITE);
  });

  const isOfficeConnected = computed(() => {
    return isCloudAppConnected(Service.OFFICE_365);
  });

  const actionNotAllowed = computed(() => {
    return isWorkspaceFrozenOrActionRestricted(
      RolePermissionsScope.PROTECTION,
      SubscriptionModule.SECURITY_AWARENESS_TRAINING
    );
  });

  const daysToAllowList = computed(() => {
    return metadata.value?.daysToAllowList || 28;
  });

  const handleAllowButtonClick = async () => {
    await securityAwarenessStore.createSatConfig();
    const config = componentDialogsConfigConstructor({
      width: ModalWidth.LARGE,
      action: SecurityAwarenessAction.ALLOW_CLOUD_SERVICE,
      item: {},
      component: AllowSecurityAwarenessModal,
      disable: actionNotAllowed.value,
      callback: () => {},
    });

    dialogsStore.openDialog({
      ...config,
      header: {
        title: i18n.global.t(`modals.${SecurityAwarenessAction.ALLOW_CLOUD_SERVICE}.title`),
        close: true,
      },
      footer: {
        buttons: [
          {
            title: i18n.global.t("general.close"),
            spacer: "before",
            color: "primary",
            cancel: true,
          },
        ],
      },
    });
  };

  const loadSecurityAwarenessEmptyStateData = async () => {
    showEmptyStateSkeletonLoader.value = true;
    await securityAwarenessStore.getMetadata();
    await securityAwarenessStore.getSecurityAwarenessSettings();
    await cloudAppsStore.getServices();

    showEmptyStateSkeletonLoader.value = false;
  };

  return {
    handleAllowButtonClick,
    showEmptyStateSkeletonLoader,
    isCloudAppsConnected,
    daysToAllowList,
    loadSecurityAwarenessEmptyStateData,
    isGoogleConnected,
    isOfficeConnected,
  };
}
